import { useState } from 'react'
import type { DraftRecording, Submission } from '@/types'
import { useStore } from '@/stores'
import {
  getAssetById,
  getIndicationById,
  getKiqById,
} from '@/stores/taxonomyStore'
import { isOtherTaxonomyId } from '@/lib/taxonomySelection'
import { getSubmissionById, saveSubmission } from '@/lib/db'
import { requestSubmissionDeletion, cancelSubmissionDeletion } from '@/lib/api'
import { useToast } from '@/components/providers/ToastProvider'
import { NoteModal } from '../RecordingList/NoteModal'
import { TranscriptModal } from '../RecordingList/TranscriptModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog'
import { PendingDeletionBanner } from './PendingDeletionBanner'
import { StatusBadge } from './StatusBadge'
import { PlaybackErrorAlert } from './PlaybackErrorAlert'
import { RecordingMetadata } from './RecordingMetadata'
import { RecordingControls } from './RecordingControls'
import { formatElapsed, getDisplayDate } from './formatters'
import { deriveRecordingName } from './recordingName'
import { useBlobPreparation } from './hooks/useBlobPreparation'
import { usePlaybackSession } from './hooks/usePlaybackSession'
import { resolveDeleteAction } from './deleteAction'

interface RecordingItemProps {
  recording: DraftRecording | Submission
  onDelete: () => void
  onPlaybackStart?: (id: string) => void
  onPlaybackStop?: (id: string) => void
  currentPlayingId?: string | null
  // Hide the delete control (e.g. AWS-only history rows with no local copy to
  // remove). Defaults to shown, preserving recorder-list behavior.
  canDelete?: boolean
  // Enable server-side soft-delete (3-day retention) for synced submissions —
  // the My Submissions history surface. Off elsewhere, so the recorder list
  // keeps its immediate local-delete behavior.
  allowServerDelete?: boolean
}

export function RecordingItem({
  recording,
  onDelete,
  onPlaybackStart,
  onPlaybackStop,
  currentPlayingId,
  canDelete = true,
  allowServerDelete = false,
}: RecordingItemProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCancellingDeletion, setIsCancellingDeletion] = useState(false)
  const updateSubmission = useStore((state) => state.updateSubmission)
  const { showToast } = useToast()

  const isDraft = 'blob' in recording
  const isSubmission = !isDraft
  const isTextNote = isSubmission && 'type' in recording && recording.type === 'text'
  const noteBody = isTextNote && 'noteBody' in recording ? recording.noteBody : null
  const noteBodyEditedAt =
    isTextNote && 'noteBodyEditedAt' in recording ? recording.noteBodyEditedAt ?? null : null
  const isLongNote = Boolean(noteBody && noteBody.length > 200)
  // Present only when the recording's binder opts in (server-gated).
  const transcript =
    isSubmission && 'transcript' in recording ? recording.transcript ?? null : null
  const transcriptEditedAt =
    isSubmission && 'transcriptEditedAt' in recording
      ? recording.transcriptEditedAt ?? null
      : null

  // Server-gated: the owner may delete this submission (binder allowDelete).
  const deletionEnabled = isSubmission && recording.deletionEnabled === true
  // Server-gated: false once the asset's active period has closed. The server
  // also forces deletionEnabled off and rejects edits (403); this hides the
  // edit affordance so the user isn't offered an action that would fail.
  const assetClosed = isSubmission && recording.assetActive === false
  const isPendingDeletion =
    isSubmission && recording.deletionStatus === 'pending'
  const deletionExpiresAt =
    isSubmission && recording.deletionExpiresAt ? recording.deletionExpiresAt : null

  // Prefer the on-device blob for playback (instant, offline-capable). Once the
  // retention window elapses the local bytes are purged, and playback falls back
  // to the remote S3 object via a presigned URL (see isRemoteAudio below).
  const submissionLocalBlob =
    isSubmission && 'localBlob' in recording ? (recording.localBlob as Blob | undefined | null) : null
  const blobSource = isDraft ? recording.blob : submissionLocalBlob ?? null
  const chunkTotal = recording.chunkCount ?? recording.chunkIds?.length ?? 0
  const chunkSessionId =
    recording.chunkSessionId ??
    (recording.chunkIds && recording.chunkIds.length > 0 ? recording.id : null)
  // Purging local audio also deletes the session's retained chunks, so a stale
  // chunk reference must not count as a playable local source afterward.
  const isPurged = isSubmission && recording.localAudioPurged === true
  const hasChunkSource = !isPurged && Boolean(chunkSessionId && chunkTotal > 0)
  const syncedAt = isSubmission ? recording.syncedAt : null
  const isSynced = Boolean(syncedAt)
  const processingStatus =
    isSubmission && recording.processingStatus
      ? recording.processingStatus
      : isSynced
        ? 'completed'
        : 'queued'

  const isAudioRecording = !isTextNote
  // No local bytes, but the recording is safely on the server → stream from S3.
  const isRemoteAudio =
    isSubmission &&
    isAudioRecording &&
    !blobSource &&
    isSynced &&
    (isPurged || processingStatus === 'completed')

  const { playbackBlob, playbackSupported, setPlaybackBlob, setPlaybackSupported } =
    useBlobPreparation({
      blobSource,
      hasChunkSource,
      isRemoteAudio,
      isDraft,
      recordingId: recording.id,
    })

  const hasPlayableAudio =
    (playbackSupported && (Boolean(playbackBlob) || hasChunkSource)) || isRemoteAudio
  const canDownloadAudio =
    isAudioRecording && (Boolean(blobSource) || hasChunkSource || isRemoteAudio)

  const {
    audioRef,
    audioUrl,
    isPlaying,
    isPreparingPlayback,
    playbackError,
    elapsedSeconds,
    setIsPlaying,
    setPlaybackError,
    handlePlay,
    handleStop,
    handleDelete,
    handleDownload,
    handleEnded,
    handleRetrySync,
    handleAudioElementError,
  } = usePlaybackSession({
    recording,
    isDraft,
    blobSource,
    hasChunkSource,
    isRemoteAudio,
    chunkSessionId,
    canDownloadAudio,
    playbackBlob,
    setPlaybackBlob,
    setPlaybackSupported,
    onPlaybackStart,
    onPlaybackStop,
    onDelete,
    currentPlayingId,
  })

  const asset = getAssetById(recording.assetId)
  const indication = getIndicationById(recording.indicationId)
  const assetLabel = isOtherTaxonomyId(recording.assetId) ? 'Other' : asset?.label ?? null
  const indicationLabel = isOtherTaxonomyId(recording.indicationId)
    ? 'Other'
    : indication?.label ?? null
  const recordingName = deriveRecordingName({
    assetLabel,
    indicationLabel,
    isTextNote,
    noteBody,
  })

  const kiqs = recording.kiqIds
    .map((id) => getKiqById(id))
    .filter((kiq): kiq is NonNullable<typeof kiq> => kiq !== undefined)
  const hasOtherKiqOnly =
    recording.kiqIds.length === 1 && isOtherTaxonomyId(recording.kiqIds[0])

  // Temporary "Insight Type" selections captured with the submission/draft.
  const insightTags = recording.insightTags ?? []

  const displayDate = getDisplayDate(recording.createdAt)

  // Reflect an edited transcript in the store (so every list re-renders) and
  // persist to IndexedDB when a local copy exists. AWS-only history rows have no
  // local copy — the store update is enough for the session and the server is
  // the source of truth on the next remote load.
  const handleTranscriptSaved = (updated: Submission) => {
    updateSubmission(recording.id, {
      transcript: updated.transcript ?? null,
      transcriptEditedAt: updated.transcriptEditedAt ?? null,
      updatedAt: updated.updatedAt,
    })
    void (async () => {
      const local = await getSubmissionById(recording.id)
      if (!local) return
      await saveSubmission({
        ...local,
        transcript: updated.transcript ?? null,
        transcriptEditedAt: updated.transcriptEditedAt ?? null,
        updatedAt: updated.updatedAt ?? local.updatedAt,
      })
    })()
  }

  // Reflect an edited note body in the store (so every list re-renders) and
  // persist to IndexedDB when a local copy exists. Mirrors handleTranscriptSaved:
  // AWS-only history rows have no local copy, so the store update carries the
  // session and the server is the source of truth on the next remote load.
  const handleNoteSaved = (updated: Submission) => {
    updateSubmission(recording.id, {
      noteBody: updated.noteBody ?? undefined,
      textPreview: updated.textPreview ?? undefined,
      tags: updated.tags ?? undefined,
      noteBodyEditedAt: updated.noteBodyEditedAt ?? null,
      updatedAt: updated.updatedAt,
    })
    void (async () => {
      const local = await getSubmissionById(recording.id)
      if (!local) return
      await saveSubmission({
        ...local,
        noteBody: updated.noteBody ?? local.noteBody,
        textPreview: updated.textPreview ?? local.textPreview,
        tags: updated.tags ?? local.tags,
        noteBodyEditedAt: updated.noteBodyEditedAt ?? null,
        updatedAt: updated.updatedAt ?? local.updatedAt,
      })
    })()
  }

  // Persist refreshed deletion state to IndexedDB when a local copy exists, so
  // the pending state survives reload offline (remote-only rows just live in
  // the store for the session and re-sync on the next remote load).
  const persistDeletionState = async (updated: Submission) => {
    const local = await getSubmissionById(recording.id)
    if (!local) return
    await saveSubmission({
      ...local,
      deletionStatus: updated.deletionStatus ?? null,
      deletionExpiresAt: updated.deletionExpiresAt ?? null,
      deletionEnabled: updated.deletionEnabled ?? local.deletionEnabled,
      updatedAt: updated.updatedAt ?? local.updatedAt,
    })
  }

  const handleCancelDeletion = async () => {
    setIsCancellingDeletion(true)
    try {
      const updated = await cancelSubmissionDeletion(recording.id)
      updateSubmission(recording.id, {
        deletionStatus: updated.deletionStatus ?? null,
        deletionExpiresAt: updated.deletionExpiresAt ?? null,
        updatedAt: updated.updatedAt,
      })
      await persistDeletionState(updated)
      showToast('Deletion cancelled', 'success')
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to cancel deletion',
        'error',
      )
    } finally {
      setIsCancellingDeletion(false)
    }
  }

  const handleConfirmServerDelete = async () => {
    setIsDeleting(true)
    try {
      const updated = await requestSubmissionDeletion(recording.id)
      updateSubmission(recording.id, {
        deletionStatus: updated.deletionStatus ?? 'pending',
        deletionExpiresAt: updated.deletionExpiresAt ?? null,
        deletionEnabled: updated.deletionEnabled ?? true,
        updatedAt: updated.updatedAt,
      })
      await persistDeletionState(updated)
      setIsDeleteDialogOpen(false)
      showToast('Recording scheduled for deletion', 'success', 6000, {
        label: 'Undo',
        onClick: () => void handleCancelDeletion(),
      })
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to delete recording',
        'error',
      )
    } finally {
      setIsDeleting(false)
    }
  }

  // What the trash control does for this row (see resolveDeleteAction).
  const deleteAction = resolveDeleteAction({
    isPendingDeletion,
    isDraft,
    canDelete,
    allowServerDelete,
    deletionEnabled,
  })

  const handleDeleteClick = () => {
    if (deleteAction === 'server') {
      setIsDeleteDialogOpen(true)
    } else if (deleteAction === 'local') {
      void handleDelete()
    }
  }

  return (
    <article
      className="w-full bg-navy-light rounded-xl p-4 border border-navy-light"
      aria-labelledby={`recording-title-${recording.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <RecordingMetadata
          recordingId={recording.id}
          recordingName={recordingName}
          isDraft={isDraft}
          isTextNote={isTextNote}
          noteBody={noteBody}
          isLongNote={isLongNote}
          onOpenNote={() => setIsModalOpen(true)}
          durationMs={recording.durationMs ?? 0}
          hasPlayableAudio={hasPlayableAudio}
          displayDate={displayDate}
          blobSource={blobSource}
          playbackSupported={playbackSupported}
          kiqs={kiqs}
          hasOtherKiqOnly={hasOtherKiqOnly}
          insightTags={insightTags}
          statusBadge={
            <StatusBadge
              isSynced={isSynced}
              processingStatus={processingStatus}
              syncedAt={syncedAt}
              onRetrySync={handleRetrySync}
            />
          }
        />

        {hasPlayableAudio && (
          <audio
            ref={audioRef}
            src={audioUrl ?? undefined}
            preload="auto"
            onEnded={handleEnded}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={handleAudioElementError}
          />
        )}

        <RecordingControls
          isDraft={isDraft}
          canDelete={deleteAction !== null}
          canDownloadAudio={canDownloadAudio}
          hasPlayableAudio={hasPlayableAudio}
          isPlaying={isPlaying}
          isPreparingPlayback={isPreparingPlayback}
          onDelete={handleDeleteClick}
          onDownload={handleDownload}
          onPlay={handlePlay}
          onStop={handleStop}
        />
      </div>

      {isPendingDeletion && (
        <PendingDeletionBanner
          expiresAt={deletionExpiresAt}
          onCancel={() => void handleCancelDeletion()}
          isCancelling={isCancellingDeletion}
        />
      )}

      {playbackError && (
        <PlaybackErrorAlert
          message={playbackError}
          onRetry={() => {
            setPlaybackError(null)
            void handlePlay()
          }}
        />
      )}

      {hasPlayableAudio && (
        <div
          className="mt-3 text-xs text-gray-300"
          aria-live="polite"
          aria-label="Elapsed playback time"
        >
          Elapsed: {formatElapsed(elapsedSeconds)}
        </div>
      )}

      {transcript && (
        <div className="mt-3">
          <button
            onClick={() => setIsTranscriptOpen(true)}
            className="text-xs text-neon-cyan hover:text-neon-cyan/80 underline focus:outline-none focus:ring-2 focus:ring-neon-cyan rounded"
            aria-label="View transcript"
          >
            View transcript
          </button>
        </div>
      )}

      {isTextNote && noteBody && isSubmission && (
        <NoteModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          submissionId={recording.id}
          noteBody={noteBody}
          noteBodyEditedAt={noteBodyEditedAt}
          title={recordingName}
          createdAt={displayDate}
          onSaved={handleNoteSaved}
          canEdit={!assetClosed}
        />
      )}

      {transcript && isSubmission && (
        <TranscriptModal
          isOpen={isTranscriptOpen}
          onClose={() => setIsTranscriptOpen(false)}
          submissionId={recording.id}
          transcript={transcript}
          transcriptEditedAt={transcriptEditedAt}
          title={`${recordingName} — Transcript`}
          createdAt={displayDate}
          onSaved={handleTranscriptSaved}
          canEdit={!assetClosed}
        />
      )}

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => void handleConfirmServerDelete()}
        title={`Delete "${recordingName}"?`}
        description={
          <>
            This recording will be permanently deleted in 3 days, along with its
            audio and transcript. You can cancel any time before then.
          </>
        }
        confirmLabel="Delete"
        danger
        loading={isDeleting}
      />
    </article>
  )
}
